import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Wallet, Download, Upload, Search, Loader2, Pencil, Plus, Trash2, X,
  History, TrendingUp, TrendingDown, Building2, Calendar,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { formatDate, formatDateToLocal, getTodayLocal } from '../../lib/dateUtils';

const inr = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0,
  }).format(n);
};

/**
 * Salary Register — visible ONLY to super-admin and HR.
 * Combines: filterable roster table, per-employee history drawer,
 * inline add/edit/delete of increments, and Excel round-trip.
 */
export default function SalaryRegisterPage({ api }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');
  const [detailEmp, setDetailEmp] = useState(null); // { employee_id, name }
  const [showAddFor, setShowAddFor] = useState(null); // employee id
  const [editCurrentFor, setEditCurrentFor] = useState(null); // {employee_id, name}
  const [uploading, setUploading] = useState(false);

  // Fetch the latest increment for a given employee so the "Edit current" form
  // can be pre-populated — falls back to `null` so the form starts blank if
  // no history yet.
  const openEditCurrent = async (row) => {
    if (!row.current_salary) {
      // No history yet — just open a fresh add-form
      setShowAddFor(row.employee_id);
      return;
    }
    try {
      const res = await api.get(`/api/salary/employees/${row.employee_id}/history`);
      const latest = (res.data.increments || [])[0];
      if (!latest) {
        setShowAddFor(row.employee_id);
        return;
      }
      setEditCurrentFor({ employee_id: row.employee_id, name: row.name, initial: latest });
    } catch {
      toast.error('Failed to open current salary editor');
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/salary/register');
      setRows(res.data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load salary register');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); }, []);

  const departments = useMemo(() => (
    ['all', ...Array.from(new Set(rows.map(r => r.department).filter(Boolean)))]
  ), [rows]);

  const filtered = useMemo(() => rows.filter(r => {
    if (deptFilter !== 'all' && r.department !== deptFilter) return false;
    if (q) {
      const n = q.toLowerCase();
      return (r.name || '').toLowerCase().includes(n) ||
             (r.work_email || '').toLowerCase().includes(n) ||
             (r.designation || '').toLowerCase().includes(n);
    }
    return true;
  }), [rows, q, deptFilter]);

  const totals = useMemo(() => {
    const totalCurrent = filtered.reduce((s, r) => s + (Number(r.current_salary) || 0), 0);
    const withSalary = filtered.filter(r => r.current_salary != null).length;
    return { totalCurrent, withSalary, total: filtered.length };
  }, [filtered]);

  const handleExport = async () => {
    try {
      const res = await api.get('/api/salary/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `salary_register_${Date.now()}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Exported');
    } catch { toast.error('Export failed'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/api/salary/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { added, skipped, errors } = res.data;
      toast.success(`Imported ${added} increment${added !== 1 ? 's' : ''}` + (skipped ? ` · ${skipped} skipped` : ''));
      if (errors?.length) toast.warning(`${errors.length} row error(s) — check console`);
      if (errors?.length) console.warn('Salary import errors:', errors);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]" data-testid="salary-loading">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-[1400px]" data-testid="salary-register-page">
      {/* Hero */}
      <div className="mb-6 rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-teal-50 border border-emerald-100/70 p-6 md:p-8 shadow-soft">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100/70 text-emerald-800 text-xs font-semibold mb-3">
              <Wallet className="w-3.5 h-3.5" /> Salary Register · Restricted
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-neutral-900 tracking-tight">Compensation & Increments</h1>
            <p className="text-sm text-neutral-600 mt-2 max-w-xl">
              Visible only to super-admin and HR. Track each employee&apos;s current salary and every increment
              — updateable inline or via Excel round-trip.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={handleExport} data-testid="salary-export-btn">
              <Download className="w-4 h-4 mr-1.5" /> Download Excel
            </Button>
            <label className={`inline-flex items-center gap-1.5 h-10 px-4 rounded-xl border border-neutral-200 bg-white text-sm font-medium cursor-pointer hover:bg-neutral-50 ${uploading ? 'opacity-50 pointer-events-none' : ''}`} data-testid="salary-import-label">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload Excel
              <input type="file" accept=".xlsx,.xlsm" className="hidden" onChange={handleImport} disabled={uploading} data-testid="salary-import-input" />
            </label>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div className="bg-white/70 rounded-xl p-3 border border-emerald-100">
            <p className="text-neutral-500 mb-0.5">Employees</p>
            <p className="text-lg font-bold text-neutral-900">{totals.total}</p>
          </div>
          <div className="bg-white/70 rounded-xl p-3 border border-emerald-100">
            <p className="text-neutral-500 mb-0.5">With salary on record</p>
            <p className="text-lg font-bold text-neutral-900">{totals.withSalary}</p>
          </div>
          <div className="bg-white/70 rounded-xl p-3 border border-emerald-100 col-span-2 md:col-span-1">
            <p className="text-neutral-500 mb-0.5">Total annual cost</p>
            <p className="text-lg font-bold text-neutral-900">{inr(totals.totalCurrent)}</p>
          </div>
          <div className="bg-white/70 rounded-xl p-3 border border-emerald-100 col-span-2 md:col-span-1">
            <p className="text-neutral-500 mb-0.5">Monthly cost</p>
            <p className="text-lg font-bold text-neutral-900">{inr(totals.totalCurrent / 12)}</p>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input placeholder="Search by name, email, designation…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white" data-testid="salary-search" />
        </div>
        <SearchableSelect
          value={deptFilter}
          onChange={setDeptFilter}
          triggerClassName="h-10 min-w-[180px] rounded-xl border-neutral-200"
          options={departments.map(d => ({ value: d, label: d === 'all' ? 'All departments' : d }))}
          data-testid="salary-dept-filter"
        />
        <span className="text-xs text-neutral-500 ml-auto">{filtered.length} of {totals.total}</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200 text-xs uppercase tracking-wide text-neutral-500">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Name</th>
                <th className="px-4 py-3 text-right font-semibold">Monthly CTC</th>
                <th className="px-4 py-3 text-right font-semibold">Annual CTC</th>
                <th className="px-4 py-3 text-left font-semibold">Effective Date</th>
                <th className="px-4 py-3 text-left font-semibold">Last Increment %</th>
                <th className="px-4 py-3 text-right font-semibold">Previous Annual</th>
                <th className="px-4 py-3 text-left font-semibold">Designation</th>
                <th className="px-4 py-3 text-left font-semibold">Department</th>
                <th className="px-4 py-3 text-right font-semibold w-32">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-12 text-neutral-400" data-testid="salary-empty-row">No employees match your filters.</td>
                </tr>
              )}
              {filtered.map(r => (
                <tr key={r.employee_id} className="hover:bg-neutral-50" data-testid={`salary-row-${r.employee_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-neutral-900">{r.name}</div>
                    {r.work_email && <div className="text-xs text-neutral-500 truncate max-w-[220px]">{r.work_email}</div>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap" data-testid={`salary-monthly-${r.employee_id}`}>
                    {r.current_salary != null ? (
                      <span className="font-semibold text-emerald-700">{inr(r.current_salary / 12)}</span>
                    ) : (
                      <span className="text-neutral-400 text-xs italic">Not set</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap" data-testid={`salary-annual-${r.employee_id}`}>
                    {r.current_salary != null ? (
                      <span className="font-semibold text-neutral-900">{inr(r.current_salary)}</span>
                    ) : (
                      <span className="text-neutral-400 text-xs italic">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-neutral-700 text-xs">{r.increment_date ? formatDate(r.increment_date) : <span className="text-neutral-400">—</span>}</td>
                  <td className="px-4 py-3">
                    {r.increment_percent != null ? (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${r.increment_percent >= 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                        {r.increment_percent >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {r.increment_percent.toFixed(1)}%
                      </span>
                    ) : <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-neutral-700 text-xs whitespace-nowrap">{r.old_salary != null ? inr(r.old_salary) : <span className="text-neutral-400">—</span>}</td>
                  <td className="px-4 py-3 text-neutral-700 text-xs">{r.designation || <span className="text-neutral-400">—</span>}</td>
                  <td className="px-4 py-3 text-neutral-700 text-xs">{r.department || <span className="text-neutral-400">—</span>}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => openEditCurrent(r)} title="Edit current salary" className="p-1.5 hover:bg-blue-100 rounded-lg text-blue-700" data-testid={`salary-edit-current-${r.employee_id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => setDetailEmp({ employee_id: r.employee_id, name: r.name })} title="History & past salaries" className="p-1.5 hover:bg-neutral-100 rounded-lg text-neutral-500 hover:text-neutral-900" data-testid={`salary-history-${r.employee_id}`}>
                        <History className="w-4 h-4" />
                      </button>
                      <button onClick={() => setShowAddFor(r.employee_id)} title="Add new increment" className="p-1.5 hover:bg-emerald-100 rounded-lg text-emerald-700" data-testid={`salary-add-${r.employee_id}`}>
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {detailEmp && (
        <HistoryDrawer
          api={api}
          employee={detailEmp}
          onClose={() => setDetailEmp(null)}
          onChanged={() => { fetchAll(); }}
        />
      )}

      {showAddFor && (
        <IncrementForm
          api={api}
          employeeId={showAddFor}
          initial={null}
          onClose={() => setShowAddFor(null)}
          onSaved={() => { setShowAddFor(null); fetchAll(); }}
        />
      )}

      {editCurrentFor && (
        <IncrementForm
          api={api}
          employeeId={editCurrentFor.employee_id}
          initial={editCurrentFor.initial}
          heading={`Edit current salary · ${editCurrentFor.name}`}
          onClose={() => setEditCurrentFor(null)}
          onSaved={() => { setEditCurrentFor(null); fetchAll(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function HistoryDrawer({ api, employee, onClose, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/salary/employees/${employee.employee_id}/history`);
      setItems(res.data.increments || []);
    } catch (e) { toast.error('Failed to load history'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchHistory(); }, [employee.employee_id]);

  const handleDelete = async (inc) => {
    if (!window.confirm(`Delete increment on ${formatDate(inc.increment_date)}?`)) return;
    try {
      await api.delete(`/api/salary/increments/${inc._id}`);
      toast.success('Deleted');
      fetchHistory();
      onChanged();
    } catch (e) { toast.error('Delete failed'); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-end" onClick={onClose} data-testid="salary-history-drawer">
      <div className="bg-white h-full w-full max-w-lg shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-neutral-100 flex items-start justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold text-neutral-900">{employee.name}</h2>
            <p className="text-xs text-neutral-500 mt-0.5">Full increment history</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full" data-testid="salary-history-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-2 gap-2 mb-4">
            <Button size="sm" onClick={() => { setShowAdd({ mode: 'new' }); }} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="salary-drawer-add">
              <Plus className="w-4 h-4 mr-1.5" /> Add New Hike
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setShowAdd({ mode: 'past' }); }} data-testid="salary-drawer-add-past">
              <Calendar className="w-4 h-4 mr-1.5" /> Add Past Salary
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin text-emerald-600 mx-auto" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-sm text-neutral-500">No increments recorded yet.</div>
          ) : (
            <div className="space-y-3">
              {items.map(inc => (
                <Card key={inc._id} className="p-4" data-testid={`salary-drawer-item-${inc._id}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="text-lg font-bold text-neutral-900">{inr(inc.new_salary)}</div>
                      <div className="text-xs text-neutral-500">from {inr(inc.old_salary)}</div>
                    </div>
                    {inc.increment_percent != null && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${inc.increment_percent >= 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                        {inc.increment_percent >= 0 ? '+' : ''}{inc.increment_percent.toFixed(1)}%
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-neutral-600 flex items-center gap-1 mb-1"><Calendar className="w-3 h-3" />{formatDate(inc.increment_date)}</div>
                  {inc.notes && <p className="text-xs text-neutral-500 italic mt-1">{inc.notes}</p>}
                  {inc.updated_by_name && <p className="text-[10px] text-neutral-400 mt-1">Last updated by {inc.updated_by_name}</p>}
                  <div className="flex gap-1 mt-3 pt-2 border-t border-neutral-100">
                    <Button size="sm" variant="outline" onClick={() => setEditing(inc)} className="text-xs h-7" data-testid={`salary-drawer-edit-${inc._id}`}>
                      <Pencil className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDelete(inc)} className="text-xs h-7 text-red-600 hover:bg-red-50" data-testid={`salary-drawer-delete-${inc._id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {(showAdd || editing) && (
          <IncrementForm
            api={api}
            employeeId={employee.employee_id}
            initial={editing}
            heading={editing ? 'Edit Increment' : (showAdd?.mode === 'past' ? 'Add Past Salary' : 'Add New Hike')}
            defaultPast={showAdd?.mode === 'past'}
            onClose={() => { setShowAdd(false); setEditing(null); }}
            onSaved={() => { setShowAdd(false); setEditing(null); fetchHistory(); onChanged(); }}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function IncrementForm({ api, employeeId, initial, heading, defaultPast, onClose, onSaved }) {
  const isEdit = !!initial?._id;
  // Monthly is the PRIMARY input. Annual mirrors it (monthly × 12). Users can
  // type into either — the other stays in sync.
  const initialAnnual = initial?.new_salary || '';
  const initialMonthly = initialAnnual ? String(Math.round(Number(initialAnnual) / 12)) : '';
  const [monthly, setMonthly] = useState(initialMonthly);
  const [annual, setAnnual] = useState(initialAnnual ? String(initialAnnual) : '');
  const [oldSalary, setOldSalary] = useState(initial?.old_salary || '');
  const initialDate = initial?.increment_date
    ? initial.increment_date.slice(0, 10)
    : (defaultPast
        // For "Add past salary" default to one year ago as a hint
        ? formatDateToLocal(new Date(Date.now() - 365 * 86400000))
        : getTodayLocal());
  const [incDate, setIncDate] = useState(initialDate);
  const [notes, setNotes] = useState(initial?.notes || '');
  const [saving, setSaving] = useState(false);

  const setMonthlyLinked = (v) => {
    setMonthly(v);
    const n = Number(v);
    setAnnual(v === '' || Number.isNaN(n) ? '' : String(Math.round(n * 12)));
  };
  const setAnnualLinked = (v) => {
    setAnnual(v);
    const n = Number(v);
    setMonthly(v === '' || Number.isNaN(n) ? '' : String(Math.round(n / 12)));
  };

  const derivedPct = useMemo(() => {
    const o = Number(oldSalary); const n = Number(annual);
    if (!o || !n) return null;
    return ((n - o) / o * 100).toFixed(1);
  }, [oldSalary, annual]);

  const submit = async () => {
    if (!monthly || Number(monthly) <= 0) return toast.error('Monthly CTC is required');
    if (!incDate) return toast.error('Effective date required');
    setSaving(true);
    try {
      const payload = {
        monthly_salary: Number(monthly),
        old_salary: oldSalary ? Number(oldSalary) : undefined,
        increment_date: new Date(incDate + 'T00:00:00Z').toISOString(),
        notes,
      };
      if (isEdit) {
        await api.patch(`/api/salary/increments/${initial._id}`, payload);
      } else {
        await api.post(`/api/salary/employees/${employeeId}/increments`, payload);
      }
      toast.success(isEdit ? 'Salary updated' : 'Salary recorded');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="salary-form-backdrop">
      <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="salary-form">
        <div className="p-6 border-b border-neutral-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-neutral-900">{heading || (isEdit ? 'Edit Increment' : 'Add Increment')}</h2>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full" data-testid="salary-form-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Monthly CTC (₹) *</label>
              <Input type="number" value={monthly} onChange={(e) => setMonthlyLinked(e.target.value)} placeholder="e.g. 100000" data-testid="salary-form-monthly" />
              {monthly && !Number.isNaN(Number(monthly)) && (
                <p className="text-[11px] text-neutral-500 mt-1">{inr(monthly)}/month</p>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Annual (auto)</label>
              <Input type="number" value={annual} onChange={(e) => setAnnualLinked(e.target.value)} placeholder="Auto: monthly × 12" data-testid="salary-form-annual" />
              {annual && !Number.isNaN(Number(annual)) && (
                <p className="text-[11px] text-neutral-500 mt-1">{inr(annual)}/year</p>
              )}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Effective date *</label>
            <Input type="date" value={incDate} onChange={(e) => setIncDate(e.target.value)} data-testid="salary-form-date" />
            <p className="text-[11px] text-neutral-500 mt-1">
              {defaultPast
                ? 'Pick the date this past salary took effect.'
                : 'Newer than the latest entry? Employee profile CTC will update automatically.'}
            </p>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Previous salary (₹, annual) — optional</label>
            <Input type="number" value={oldSalary} onChange={(e) => setOldSalary(e.target.value)} placeholder="Auto-filled from previous record" data-testid="salary-form-old" />
          </div>

          {derivedPct != null && (
            <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-xl text-xs text-emerald-800">
              Computed increment vs previous: <span className="font-bold">{derivedPct}%</span>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Notes</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="e.g. Annual raise, promotion to Senior…" data-testid="salary-form-notes" />
          </div>
        </div>
        <div className="p-6 border-t border-neutral-100 bg-neutral-50/50 rounded-b-3xl flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} data-testid="salary-form-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="salary-form-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? 'Save Changes' : 'Save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
